import React, {useEffect, useRef, useState} from 'react';
import PopupModal from "../../../../../../../../../components/Modal/PopupModal";
import {useTranslation} from "react-i18next";
import {ReactComponent as PinIco} from "../../../../../../../../../../images/icons/pin-outline.svg";
import {ReactComponent as LocationIco} from "../../../../../../../../../../images/icons/location.svg";
import LocationForm from "../../../../../../../../../components/Location/LocationForm";
import {ApiError, ConfigAddressApi, ConfigApi} from "../../../../../../../../../services/Api/types";
import {SessionState} from "../../../../../../../../../services/Session/types";
import {api} from "../../../../../../../../../services/Api/api";
import {useGeolocation} from "../../../../../../../../../hooks/useGeolocation";
import {useSessionService} from "../../../../../../../../../services/Session/sessionService";
import {LocationFormRef} from "../../../../../../../../../services/types";

interface Props {
    show: boolean;
    onHide: () => void;
    config: ConfigApi | undefined;
    session: SessionState;
    setErrors: React.Dispatch<React.SetStateAction<ApiError[]>>;
    errors: ApiError[];
    address: ConfigAddressApi | undefined;
}

const MultistoreWithHomePageAddressModal = ({show, onHide, config, session, setErrors, errors, address}: Props) => {
    const {t} = useTranslation();
    const [internalAddress, setInternalAddress] = useState(address ?? session.address);
    const [forceStreet, setForceStreet] = useState(false);
    const {handleGeolocationError, getPosition} = useGeolocation();
    const [loadingLocation, setLoadingLocation] = useState(false);
    const [loading, setLoading] = useState(false);
    const sessionService = useSessionService();
    const formRef = useRef<LocationFormRef>(null);
    const [forceUpdateAddressFormValue, setForceUpdateAddressFormValue] = useState(false);

    useEffect(() => {
        if (show) {
            formRef.current?.validateAddress();
        }
    }, [show]);

    const onSubmit = async () => {
        const formErrors = formRef.current?.validateAddress();

        if (formErrors && formErrors.length > 0) return;

        try {
            setLoading(true);
            const res = await api.getLocationCheck(internalAddress);
            if (!res.coordinates) {
                setErrors([{
                    "field": "location",
                    "code": "coordinates_not_exist",
                    message: ""
                }]);
            } else if (!res.in_delivery_area) {
                setErrors([{
                    "field": "location",
                    "code": "not_in_delivery_area",
                    message: ""
                }]);
            } else {
                setErrors([]);
            }

            await sessionService.updateLocation(internalAddress);
            onHide();
        } catch (error) {
            setErrors([{
                "field": "address",
                "code": "connection_problem",
                message: ""
            }]);
        } finally {
            setLoading(false);
        }
    }

    const geolocation = async () => {
        try {
            setLoadingLocation(true);
            const position = await getPosition();
            const {latitude, longitude} = position.coords;
            const res = await api.getAddressByCoordinates({latitude, longitude});
            setInternalAddress({
                street: res.street ?? "",
                city: res.city ?? "",
                country: res.country ?? "",
                zip_code: res.zip_code ?? "",
                flat_nr: res.flat_nr ?? "",
                build_nr: res.build_nr ?? "",
            });
            setForceUpdateAddressFormValue(true);
            formRef.current?.validateAddress();
        } catch (err) {
            handleGeolocationError(err as GeolocationPositionError);
        } finally {
            setLoadingLocation(false);
        }
    }

    const isSubmitButtonDisabled = () => {
        if (forceStreet) {
            return !internalAddress.street && !internalAddress?.city && !internalAddress.build_nr;
        }

        if (internalAddress.street && !internalAddress.city) {
            return false;
        }

        return !internalAddress.city && !internalAddress.build_nr
    }

    return (
        <PopupModal
            className="multistore-with-home-page-address-modal"
            title={t("modules.multistore.field.change_of_delivery_address.title")}
            description={t("modules.multistore.field.change_of_delivery_address.description")}
            show={show}
            onHide={onHide}
            headerIcon={<PinIco/>}
            bodyContent={
                <>
                    <LocationForm
                        address={internalAddress}
                        forceStreet={forceStreet}
                        setForceStreet={setForceStreet}
                        config={config}
                        onErrors={setErrors}
                        onChange={setInternalAddress}
                        errors={errors}
                        preventCheckingAddress
                        validateAddressBeforeSubmit
                        clearRequiredInputsErrorsOnChange
                        ref={formRef}
                        setForceUpdateAddressFormValue={setForceUpdateAddressFormValue}
                        forceUpdateAddressFormValue={forceUpdateAddressFormValue}
                    />
                    <div className="horizontal-divider">
                        <span className="text-muted">{t("common.word.or")}</span>
                    </div>
                    <button
                        className={`btn btn-link btn-lg text-decoration-none w-100 btn-flex btn-spinner ${loadingLocation ? "loading" : ""}`}
                        onClick={geolocation}>
                        <LocationIco/>
                        <span className="left spinner-border spinner-border-sm"/>
                        {t("modules.multistore.action.use_current_location.title")}
                    </button>
                </>
            }
            cancelButton
            primaryButton={
                <button className={`btn btn-primary btn-spinner w-100 btn-lg ${loading ? "loading" : ""}`}
                        disabled={isSubmitButtonDisabled()}
                        onClick={onSubmit}>
                    <span className="left spinner-border spinner-border-sm"/>
                    {t("modules.multistore.action.next.title")}
                </button>
            }
        />
    )
}

export default MultistoreWithHomePageAddressModal;